import { getRuntimeConfig } from './runtime.js';
import { hydrateNativeValues, persistValue } from './native-storage.js';

const SESSION_KEY = 'dope-man-online-session-v2';
const DEVICE_KEY = 'dope-man-online-device-v1';

export class HubError extends Error {
  constructor(message, status = 0, code = 'HUB_ERROR') {
    super(message);
    this.name = 'HubError';
    this.status = status;
    this.code = code;
  }
}

function storageAvailable() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function deviceId() {
  if (!storageAvailable()) return 'dope-man-server-device';
  let value = localStorage.getItem(DEVICE_KEY);
  if (!value) {
    value = globalThis.crypto?.randomUUID?.() ?? `dope-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    persistValue(DEVICE_KEY, value);
  }
  return value;
}

function savedSession() {
  if (!storageAvailable()) return null;
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch {
    persistValue(SESSION_KEY, null);
    return null;
  }
}

function saveSession(session) {
  if (!storageAvailable()) return;
  persistValue(SESSION_KEY, session ? JSON.stringify(session) : null);
}

export class HubClient {
  constructor(config = getRuntimeConfig()) {
    this.config = config;
    this.session = savedSession();
    this.deviceId = deviceId();
  }

  get authenticated() {
    return Boolean(this.session?.access_token);
  }

  async hydrateNativeStorage() {
    if (this.config.runtime !== 'android') return this.session;
    await hydrateNativeValues([SESSION_KEY, DEVICE_KEY]);
    this.session = savedSession();
    this.deviceId = deviceId();
    return this.session;
  }

  replacePlayer(player) {
    if (!this.session) return null;
    this.session = { ...this.session, player };
    saveSession(this.session);
    return this.session;
  }

  async request(path, options = {}, retry = true) {
    const headers = new Headers(options.headers || {});
    if (options.body !== undefined && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (this.session?.access_token) headers.set('Authorization', `Bearer ${this.session.access_token}`);
    let response;
    try {
      response = await fetch(`${this.config.apiBaseUrl}${path}`, { ...options, headers });
    } catch (error) {
      const hubError = new HubError(`Server connection failed: ${error.message}`, 0, 'HUB_OFFLINE');
      hubError.path = path;
      throw hubError;
    }
    if (response.status === 401 && retry && this.session?.refresh_token) {
      const refreshed = await this.refresh().catch(() => false);
      if (refreshed) return this.request(path, options, false);
    }
    const text = await response.text();
    let body = null;
    try { body = text ? JSON.parse(text) : null; } catch { body = { detail: text }; }
    if (!response.ok) {
      const detail = body?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message ?? `Server request failed (${response.status})`;
      const code = typeof detail === 'object' ? detail?.code ?? 'HUB_ERROR' : 'HUB_ERROR';
      if (code === 'MAINTENANCE_MODE' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('dopeman-maintenance', { detail }));
      }
      if (response.status === 401) this.clearSession();
      const hubError = new HubError(message, response.status, code);
      hubError.path = path;
      hubError.response = body;
      throw hubError;
    }
    return body;
  }

  async serverInfo() {
    return this.request('/server-info', {}, false);
  }

  async probe() {
    const [health, game] = await Promise.all([
      this.request('/health', {}, false),
      this.request('/game/status', {}, false),
    ]);
    return { health, game };
  }

  async registrationStatus() {
    return this.request('/auth/registration', {}, false);
  }

  async register({ email, alias, password, inviteCode = '' }) {
    const session = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        player_alias: alias,
        invite_code: inviteCode,
        device_id: this.deviceId,
        client_version: this.config.appVersion,
        platform: this.config.runtime,
      }),
    }, false);
    this.session = session;
    saveSession(session);
    return session;
  }

  async login(email, password) {
    const session = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        device_id: this.deviceId,
        client_version: this.config.appVersion,
        platform: this.config.runtime,
      }),
    }, false);
    this.session = session;
    saveSession(session);
    return session;
  }

  async refresh() {
    if (!this.session?.refresh_token) return false;
    const refreshToken = this.session.refresh_token;
    const response = await fetch(`${this.config.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refresh_token: refreshToken,
        device_id: this.deviceId,
        client_version: this.config.appVersion,
        platform: this.config.runtime,
      }),
    }).catch(() => null);
    if (!response?.ok) {
      this.clearSession();
      return false;
    }
    this.session = await response.json();
    saveSession(this.session);
    return true;
  }

  async logout() {
    if (this.authenticated) await this.request('/auth/logout', { method: 'POST' }).catch(() => null);
    this.clearSession();
  }

  clearSession() {
    this.session = null;
    saveSession(null);
  }

  async player() {
    return this.request('/player/me');
  }

  async updateAlias(playerAlias) {
    return this.request('/player/account', {
      method: 'PATCH',
      body: JSON.stringify({ player_alias: playerAlias }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/player/password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async verificationStatus() {
    return this.request('/player/verification');
  }

  async requestVerification() {
    return this.request('/player/verification/request', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async confirmVerification(code) {
    return this.request('/player/verification/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async profile() {
    return this.request('/game/profile');
  }

  async uiPreferences() {
    return this.request('/game/ui-preferences');
  }

  async updateUiPreferences({
    interfaceMode, theme, v2Theme, toastNotifications, narrationBar, toastDuration,
    reducedMotion, displayDensity, textSize, adventureLogEnabled,
    adventureLogRecentCount, adventureLogDetail, adventureLogShowRoutine,
    adventureLogPanel, adventureLogFilter,
  }) {
    const payload = {};
    if (interfaceMode !== undefined) payload.interface = interfaceMode;
    if (theme !== undefined) payload.theme = theme;
    if (v2Theme !== undefined) payload.v2_theme = v2Theme;
    if (toastNotifications !== undefined) payload.toast_notifications = toastNotifications;
    if (narrationBar !== undefined) payload.narration_bar = narrationBar;
    if (toastDuration !== undefined) payload.toast_duration = toastDuration;
    if (reducedMotion !== undefined) payload.reduced_motion = reducedMotion;
    if (displayDensity !== undefined) payload.display_density = displayDensity;
    if (textSize !== undefined) payload.text_size = textSize;
    if (adventureLogEnabled !== undefined) payload.adventure_log_enabled = adventureLogEnabled;
    if (adventureLogRecentCount !== undefined) payload.adventure_log_recent_count = adventureLogRecentCount;
    if (adventureLogDetail !== undefined) payload.adventure_log_detail = adventureLogDetail;
    if (adventureLogShowRoutine !== undefined) payload.adventure_log_show_routine = adventureLogShowRoutine;
    if (adventureLogPanel !== undefined) payload.adventure_log_panel = adventureLogPanel;
    if (adventureLogFilter !== undefined) payload.adventure_log_filter = adventureLogFilter;
    return this.request('/game/ui-preferences', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async submitBugReport(payload) {
    return this.request('/game/bug-reports', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async bugReports() {
    return this.request('/game/bug-reports');
  }

  async bugReport(reference) {
    return this.request(`/game/bug-reports/${encodeURIComponent(reference)}`);
  }

  async gameModes() {
    return this.request('/game/game-modes', {}, false);
  }

  async leaderboard({ modeId = '', difficulty = '', limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (modeId) params.set('mode_id', modeId);
    if (difficulty) params.set('difficulty', difficulty);
    params.set('limit', String(limit));
    return this.request(`/game/leaderboard?${params.toString()}`);
  }

  async leaderboardPreferences() {
    return this.request('/game/leaderboard/preferences');
  }

  async updateLeaderboardPreferences(visible) {
    return this.request('/game/leaderboard/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ visible: Boolean(visible) }),
    });
  }

  async currentRun() {
    return this.request('/game/classic/runs/current');
  }

  async startPreview(gameModeId = 'classic_30') {
    return this.request(`/game/classic/runs/start-preview?game_mode_id=${encodeURIComponent(gameModeId)}`);
  }

  async startRun(gameModeId, previewToken = '') {
    const payload = gameModeId
      ? { game_mode_id: gameModeId, preview_token: previewToken }
      : {};
    return this.request('/game/classic/runs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async resetPreview(gameModeId = 'classic_30') {
    return this.request(`/game/classic/runs/reset-preview?game_mode_id=${encodeURIComponent(gameModeId)}`);
  }

  async selfReset(previewToken, currentRunId, gameModeId = 'classic_30') {
    return this.request('/game/classic/runs/current/self-reset', {
      method: 'POST',
      body: JSON.stringify({
        preview_token: previewToken,
        current_run_id: currentRunId,
        game_mode_id: gameModeId,
      }),
    });
  }

  async restartRun() {
    return this.request('/game/classic/runs/current/restart', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async action(action, payload, expectedRevision) {
    return this.request('/game/classic/runs/current/action', {
      method: 'POST',
      body: JSON.stringify({ action, payload, expected_revision: expectedRevision }),
    });
  }
}
